import React, { useRef } from 'react';
import { canAuthorizeHistoryOrder } from '../util/gearOrderUtil';
import {Link} from 'react-router-dom';
import {Popover} from 'antd';

/**
 * Calculate and show the status of an order.
 *
 * @param props.order The Gear Order
 * @param props.userInfo The logged in user
 */
export default function OrderStatus({ order, userInfo, orderStatusLink }) {
	const popoverRef = useRef(null);

	const statusText = getOrderStatusText(order);
	const displayDetails = getDisplayDetails(statusText, order, userInfo);

	let orderStatusElement;

	let className = 'btn btn-edit';
	if (displayDetails?.className) {
		className += ' ' + displayDetails.className;
	}
	if (displayDetails?.tooltip) {
		className += ' cursor-help';
	} else {
		className += ' cursor-default';
	}

	if (!orderStatusLink) {
		orderStatusElement = (<button
				ref={popoverRef}
				type="button"
				className={className}
		>
			{statusText}
		</button>);
	} else {
		if (orderStatusLink.type === 'absolute') {
			orderStatusElement = (<button
					ref={popoverRef}
					type="button"
					className={className}
			>
				<a style={{textDecoration: 'none', color: 'inherit'}} href={orderStatusLink.link} target="_blank" rel="noopener noreferrer">
					{statusText}
					<i className="fa fa-external-link padding-x-xs"></i>
				</a>
			</button>);
		} else {
			orderStatusElement = (<Link
					ref={popoverRef}
					target="_blank"
					rel="noopener noreferrer"
					type="button"
					className={className}
					to={orderStatusLink.link}
			>
				{statusText}
				<i className="fa fa-external-link padding-x-xs"></i>
			</Link>);
		}
	}


	return (
			<>
				{displayDetails?.tooltip ?
						<Popover overlayStyle={{width: '20vw'}} placement="topRight" content={displayDetails?.tooltip}>
							{orderStatusElement}
						</Popover>
						:
						<>
							{orderStatusElement}
						</>
				}
			</>
	);
}

/**
 * Gets the Activation status of an order
 */
export const getOrderStatusText = (order) => {
	const dropShipItems = order.requisitions.filter((req) => req.patientDropship);
	const isShipToOffice = order.requisitions.filter((req) => req.shipToOffice).length > 0;
	const accessionedReqs = order.requisitions.filter((req) => req.accessionNo);
	const allReqsCanceled = order.requisitions.every((req) => req.canceled);
	let status = OrderStatuses.IN_PROGRESS;

	if (accessionedReqs.length > 0 && accessionedReqs.length === order.requisitions.length) {
		// All requisitions on the order have been accessioned
		status = OrderStatuses.ACCESSIONED;

		/**
		* Status table
		 ID   Status        Display Status
		 =    ==========    ==============
		 1    Incomplete    Received
		 2    Complete      Report Review
		 3    Printed       Printed
		 4    Reviewed      Released
		 5    PITLReview    Phy. Review
		 6    Received      Received
		 7    Error         Report Generation
		 8    QCReview      QC Review
		 9    Shipped       Shipped
	   */
		// Status 3, 4, or 9 are 'Released'
		const reqsNoteYetReleased = accessionedReqs.filter(
			(req) => req.accessionStatus !== 3 && req.accessionStatus !== 4 && req.accessionStatus !== 9
		);
		if (!reqsNoteYetReleased.length) {
			// All reqs have been released, check for Discarded Specimens
			if (order.requisitions.some((req) => req.accessionDetails?.some(accessionDetail => accessionDetail?.discardedSpecimen))) {
				status = OrderStatuses.DISCARDED_SPECIMENS;
			} else {
				status = OrderStatuses.RESULTS_READY;
			}
		}
	} else if (order.packingSlipCanceled || allReqsCanceled) {
		status = OrderStatuses.CANCELED;
	} else if (order.medicareAcknowledgementNeeded) {
		status = OrderStatuses.MEDICARE_AUTHORIZATION_REQUIRED;
	} else if (order.electronicSignatureNeeded) {
		status = OrderStatuses.AUTHORIZATION_NEEDED;
	} else if (!order.gearOrderId) {
		// SCR-7631: Req not yet started in GEAR
		if (dropShipItems.length) {
			status = OrderStatuses.WAITING_PATIENT;
		} else {
			status = OrderStatuses.PROCESSING;
		}
	} else if (!order?.workflow?.practitionerConfirmed) {
		if (order?.workflow?.patientConfirmed) {
			status = OrderStatuses.PROVIDER_ACTIVATION_NEEDED;
		} else {
			status = OrderStatuses.IN_PROGRESS;
		}
	} else if (!order?.workflow?.complete) {
		status = OrderStatuses.WAITING_PATIENT;
	} else if (isShipToOffice) {
		status = OrderStatuses.PROCESSING;
	} else {
		status = OrderStatuses.COMPLETE;
	}

	return status;
};

/**
 * Get the display details of a particular status, such as the color and tooltip.
 *
 * @param statusText The status text to be displayed
 * @param gearOrder The gear order for which the status applies
 * @param userInfo The logged in user
 */
const getDisplayDetails = (statusText, gearOrder, userInfo) => {
	const displayDetails = {
		className: null,
		tooltip: null
	};

	switch (statusText) {
		case OrderStatuses.PROVIDER_ACTIVATION_NEEDED:
			displayDetails.className = 'btn-danger';
			displayDetails.tooltip =
				"This order has been updated by your patient. Please select the Continue option to review and confirm this order.";
			break;
		case OrderStatuses.IN_PROGRESS:
			displayDetails.className = 'btn-inprogress';

			const isShipToOffice = gearOrder?.requisitions?.filter((req) => req.shipToOffice).length > 0;
			if (isShipToOffice) {
				displayDetails.tooltip =
					'This order has not been submitted for processing. Please select the Continue option to complete and submit this order.'
			} else {
				displayDetails.tooltip =
					"This order has not been submitted for processing. Please select the Continue option to complete and submit this patient's testing.";
			}
			break;
		case OrderStatuses.WAITING_PATIENT:
			displayDetails.className = 'btn-warning';
			displayDetails.tooltip = `Provider activation is complete. We are waiting for your patient
			 to complete their activation and return their samples to our lab. This status will change
			 to "${OrderStatuses.ACCESSIONED}" when the collection pack is received by our lab.`;
			break;
		case OrderStatuses.COMPLETE:
			displayDetails.className = 'btn-info';
			displayDetails.tooltip = `Provider and patient activations are complete. This status will change to "${OrderStatuses.ACCESSIONED}" when the collection pack is received by our lab.`;
			break;
		case OrderStatuses.ACCESSIONED:
			displayDetails.className = 'btn-info';
			displayDetails.tooltip =
				"We've received the patient samples and have begun testing. If you need to update or make changes to this order, please contact us at 800-522-4762.";
			break;
		case OrderStatuses.RESULTS_READY:
			displayDetails.className = 'btn-success';
			displayDetails.tooltip = 'Your lab results are now available.';
			break;
		case OrderStatuses.DISCARDED_SPECIMENS:
			displayDetails.className = 'btn-warning';
			displayDetails.tooltip =
				'We were unable to process these samples and your patient will need to recollect. If our client services team has not already done so, you may easily order a replacement collection pack from your myGDX Patient Results page.';
			break;
		case OrderStatuses.PROCESSING:
			displayDetails.className = '';
			displayDetails.tooltip =
				'These packs are not associated with a patient and will need to be activated before providing them to a patient for sample collection.';
			break;
		case OrderStatuses.AUTHORIZATION_NEEDED:
			displayDetails.className = 'btn-danger';

			// Show the status in gray and change the message if the logged in user doesn't have the ability to authorize
			if (!canAuthorizeHistoryOrder(userInfo, gearOrder)) {
				displayDetails.className = '';
				displayDetails.tooltip =
					"Authorization is required for this order. Your authorizing practitioner will need to log in and authorize this order.";
			} else if (gearOrder?.workflow?.patientConfirmed) {
				displayDetails.tooltip =
					"Your patient has completed their activation and your authorization is required for this order. Please select the Review & Authorize option to complete this patient's testing.";
			} else {
				displayDetails.tooltip =
					"Your authorization is required for this order. Please select the Review & Authorize option to complete this patient's testing.";
			}
			break;
		case OrderStatuses.MEDICARE_AUTHORIZATION_REQUIRED:
			displayDetails.className = 'btn-danger';

			if (gearOrder?.workflow?.patientConfirmed) {
				displayDetails.tooltip =
					"Your patient has provided Medicare insurance and we need to collect your medical necessity authorization. Please select the Review & Authorize option to complete this patient's Medicare testing.";
			} else {
				displayDetails.tooltip =
					"This order was submitted with Medicare insurance and we need to collect your medical necessity authorization. Please use the options menu to review and authorize this patient's Medicare testing.";
			}
			break;
		case OrderStatuses.CANCELED:
			displayDetails.className = '';
			break;
		default:
			displayDetails.className = 'btn-danger';
			break;
	}

	return displayDetails;
};

/**
 * The history status filter options. Also referenced to display the status text on a history list item.
 */
export const OrderStatuses = {
	ANY: 'Any',
	IN_PROGRESS: 'Provider Activation Incomplete', // the order was started but has not been confirmed
	PROVIDER_ACTIVATION_NEEDED: 'Provider Activation Needed', // the patient has confirmed the order but the practitioner has not
	AUTHORIZATION_NEEDED: 'Provider Signature Needed', // the order was not filled out by the authorizing practitioner and the authorizing practitioner has not signed yet
	WAITING_PATIENT: 'Patient Collection Incomplete', // One or more steps on the order are not complete and the practitioner confirmation step has been completed
	COMPLETE: 'Activation Complete', // All required workflow steps have been completed
	PROCESSING: 'Ready for Activation', // Ship to Office order has been placed
	CANCELED: 'Canceled', // order's packing slip was canceled
	ACCESSIONED: 'Testing in Progress', // The Collection pack has been scanned by the lab
	RESULTS_READY: 'Results Ready', // The results have been released
	MEDICARE_AUTHORIZATION_REQUIRED: 'Medicare Authorization Needed', // Order is for Medicare but practitioner has not acknowledged Medical Necessity
	DISCARDED_SPECIMENS: 'Discarded Specimens' // Test could not be performed and specimens were discarded
};
